import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxGameCard from './ArenaxGameCard';
import ArenaxSquadActivityFeed from './ArenaxSquadActivityFeed';
import ArenaxSquadFeed from './ArenaxSquadFeed';
import ArenaxRivalryFeed from './ArenaxRivalryFeed';
import ArenaxSquadPinnedClips from './ArenaxSquadPinnedClips';
import ArenaxRivalriesCard from './ArenaxRivalriesCard';
import ArenaxSquadOnlineIndicator from './ArenaxSquadOnlineIndicator';
import useArenaxRealtime from './hooks/useArenaxRealtime';
import { SquadAuditDrawer } from '@/components/arenax/LifecycleAuditDrawer';
import {
  Crown, Users, MapPin, Trophy, Calendar, Activity, Share2, Send, MessageCircle,
  Loader2, Copy, Plus, X, ChevronDown, AlertTriangle, Sparkles, Repeat, History,
} from 'lucide-react';
import { toast } from 'sonner';

const SPORT_GRADIENTS = {
  Football: 'linear-gradient(135deg, #10b981 0%, #f59e0b 100%)',
  Cricket: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  Badminton: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 100%)',
  Tennis: 'linear-gradient(135deg, #84cc16 0%, #06b6d4 100%)',
  Basketball: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
  Pickleball: 'linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)',
  Volleyball: 'linear-gradient(135deg, #eab308 0%, #06b6d4 100%)',
  Hockey: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
};
const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓',
};
const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function ArenaxSquadDetail() {
  const { squadId } = useParams();
  // Phase 2I — subscribe to squad realtime channel for activity feed updates
  const rt = useArenaxRealtime({ channel: 'squad', channelId: squadId, enabled: !!squadId });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [squad, setSquad] = useState(null);
  const [tab, setTab] = useState('feed');  // feed | chat | recurring
  const [recOpen, setRecOpen] = useState(false);
  const [topRivalry, setTopRivalry] = useState(null);  // Phase 2N — top rival for inline feed
  const [auditOpen, setAuditOpen] = useState(false);   // StreamX Sprint 3c — lifecycle history drawer

  const reload = async () => {
    try {
      const r = await api.get(`/arenax/squads/${squadId}`);
      setSquad(r.data);
    } catch {
      navigate('/arenax/squads');
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [squadId]);

  // Phase 2N — fetch the squad's top rivalry to render its dedicated feed inline.
  useEffect(() => {
    if (!squadId) return;
    api.get(`/arenax/squads/${squadId}/rivalries?min_matches=1&limit=1`)
      .then(r => {
        const top = (r.data?.rivalries || [])[0];
        setTopRivalry(top || null);
      })
      .catch(() => setTopRivalry(null));
  }, [squadId]);

  if (!squad) return <ArenaxLayout><div className="text-center text-slate-500 py-32">Loading squad...</div></ArenaxLayout>;

  const myMembership = (squad.members || []).find(m => m.player_id === user?.id);
  const isMember = !!myMembership;
  const isCaptain = myMembership?.role === 'captain' || myMembership?.role === 'co_captain';
  const grad = SPORT_GRADIENTS[squad.primary_sport] || 'var(--ax-primary-gradient)';

  const inviteLink = async () => {
    try {
      const r = await api.post(`/arenax/squads/${squadId}/invites`);
      const url = `${window.location.origin}/arenax/join-squad/${r.data.code}`;
      try { await navigator.clipboard.writeText(url); toast.success('Invite link copied to clipboard!'); }
      catch { toast.success(`Invite created: ${url}`); }
    } catch (err) { toast.error('Could not generate invite'); }
  };

  const onLeave = async () => {
    if (!window.confirm('Leave this squad?')) return;
    try { await api.post(`/arenax/squads/${squadId}/leave`); toast.success('You left the squad'); navigate('/arenax/squads'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not leave'); }
  };

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="arenax-squad-detail">
        {/* TEAM IDENTITY HERO */}
        <div className="rounded-3xl overflow-hidden border border-cyan-500/15 mb-7 relative" data-testid="arenax-squad-hero">
          <div className="aspect-[16/5] relative" style={{ background: grad }}>
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 75% 30%, rgba(255,255,255,0.22), transparent 60%)',
            }} />
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 18px)',
            }} />
            {/* Mega emblem */}
            <div className="absolute -bottom-8 -right-4 text-[18rem] leading-none opacity-15 select-none pointer-events-none">
              {SPORT_ICONS[squad.primary_sport] || '🏟️'}
            </div>
            {/* Content */}
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-10">
              <div className="text-[11px] tracking-[0.4em] uppercase text-white/85 font-bold mb-2">
                {squad.primary_sport} · {squad.skill_level} · {squad.city}
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white drop-shadow-2xl leading-[1.05]">
                {squad.name}
              </h1>
              {squad.bio && (
                <p className="text-sm sm:text-base text-white/90 mt-3 max-w-2xl italic">"{squad.bio}"</p>
              )}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-white/85">
                <span className="flex items-center gap-1.5 bg-white/15 px-2.5 py-1 rounded-md backdrop-blur"><Crown className="h-3.5 w-3.5 text-amber-200" />{squad.captain_name}</span>
                <span className="flex items-center gap-1.5 bg-white/15 px-2.5 py-1 rounded-md backdrop-blur"><Users className="h-3.5 w-3.5" />{squad.members_count}/{squad.max_members}</span>
                <span className="flex items-center gap-1.5 bg-white/15 px-2.5 py-1 rounded-md backdrop-blur"><Activity className="h-3.5 w-3.5 text-emerald-200" />{squad.attendance_rate?.toFixed(0)}% attendance</span>
                <span className="flex items-center gap-1.5 bg-white/15 px-2.5 py-1 rounded-md backdrop-blur"><Trophy className="h-3.5 w-3.5 text-amber-200" />{squad.games_played_total} games</span>
              </div>
            </div>
          </div>
          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-950/70 backdrop-blur border-t border-cyan-500/10">
            <div className="flex items-center gap-2">
              {isCaptain && (
                <button onClick={inviteLink} data-testid="arenax-squad-invite"
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-950 flex items-center gap-1.5"
                  style={{ background: 'var(--ax-primary-gradient)' }}>
                  <Share2 className="h-3.5 w-3.5" />Share Invite Link
                </button>
              )}
              {isCaptain && (
                <button onClick={() => setRecOpen(true)} data-testid="arenax-squad-add-recurring"
                  className="px-4 py-2 rounded-xl text-xs font-bold text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5" />Schedule Weekly Game
                </button>
              )}
              {/* StreamX Sprint 3c — Squad lifecycle history */}
              <button
                onClick={() => setAuditOpen(true)}
                data-testid="arenax-squad-history-btn"
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-300 border border-slate-500/30 hover:bg-slate-500/10 hover:text-white flex items-center gap-1.5 transition-colors"
                title="View squad lifecycle history"
              >
                <History className="h-3.5 w-3.5" />History
              </button>
            </div>
            <div className="flex items-center gap-2">
              {isMember && (
                <ArenaxSquadOnlineIndicator squadId={squadId} />
              )}
              {isMember && !isCaptain && (
                <button onClick={onLeave} data-testid="arenax-squad-leave"
                  className="px-3 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                  Leave squad
                </button>
              )}
              {!isMember && (
                <div className="text-xs text-slate-400">Have an invite link? Open it to join.</div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 border-b border-slate-800">
          <Tab id="feed" current={tab} setTab={setTab} label={`Upcoming · ${squad.upcoming_games?.length || 0}`} testid="arenax-squad-tab-feed" />
          {isMember && <Tab id="chat" current={tab} setTab={setTab} label="Squad Chat" testid="arenax-squad-tab-chat" />}
          <Tab id="members" current={tab} setTab={setTab} label={`Members · ${squad.members_count}`} testid="arenax-squad-tab-members" />
          {isCaptain && <Tab id="recurring" current={tab} setTab={setTab} label="Recurring" testid="arenax-squad-tab-recurring" />}
        </div>

        {/* Tab Bodies */}
        {tab === 'feed' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Phase 2N — Squad Heartbeat Feed (quiet + loud signals) */}
              <ArenaxSquadFeed squadId={squadId} />
              {/* Phase 2O — Pinned Highlights strip (memory anchors) */}
              <ArenaxSquadPinnedClips squadId={squadId} />
              {/* Phase 2N — Top Rivalry Feed (cinematic pulse for the squad's biggest rival) */}
              {topRivalry && (
                <ArenaxRivalryFeed
                  squadA={squadId}
                  squadB={topRivalry.opponent_id}
                  viewerSquadId={squadId}
                  limit={8}
                />
              )}
              <FeedTab squad={squad} />
            </div>
            <div className="space-y-5">
              {/* Phase 2J — Rivalries (legacy + narrative continuity) */}
              <ArenaxRivalriesCard squadId={squadId} squadName={squad.name} />
              {/* Phase 2I — Squad Activity Feed (raw event log) */}
              {isMember && (
                <ArenaxSquadActivityFeed squadId={squadId} incomingActivity={rt.squadActivity} />
              )}
            </div>
          </div>
        )}
        {tab === 'chat' && isMember && <SquadChat squadId={squadId} userId={user?.id} />}
        {tab === 'members' && <MembersTab squad={squad} isCaptain={isCaptain} reload={reload} myId={user?.id} />}
        {tab === 'recurring' && isCaptain && <RecurringTab squad={squad} reload={reload} />}

        {/* Add Recurring dialog */}
        {recOpen && <AddRecurringDialog squad={squad} onClose={() => setRecOpen(false)} onDone={() => { setRecOpen(false); reload(); }} />}
      </section>

      {/* StreamX Sprint 3c — Squad lifecycle audit drawer */}
      <SquadAuditDrawer
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        entity={squad}
      />
    </ArenaxLayout>
  );
}

function Tab({ id, current, setTab, label, testid }) {
  return (
    <button onClick={() => setTab(id)} data-testid={testid}
      className={`px-4 py-2.5 text-xs font-bold tracking-wide transition-colors relative ${
        current === id ? 'text-cyan-300' : 'text-slate-400 hover:text-slate-200'
      }`}>
      {label}
      {current === id && <span className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'var(--ax-primary-gradient)' }} />}
    </button>
  );
}

function FeedTab({ squad }) {
  const upcoming = squad.upcoming_games || [];
  return (
    <div data-testid="arenax-squad-feed">
      {upcoming.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-cyan-500/20 p-12 text-center">
          <Calendar className="h-10 w-10 text-cyan-400/50 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-200 mb-2">No games on the schedule yet</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">Captain can schedule a recurring weekly game from above — the squad shows up automatically.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {upcoming.map(g => <ArenaxGameCard key={g.id} game={g} dense />)}
        </div>
      )}
    </div>
  );
}

function MembersTab({ squad, isCaptain, reload, myId }) {
  const members = squad.members || [];
  const onPromote = async (pid) => {
    try { await api.post(`/arenax/squads/${squad.id}/members/${pid}/promote`); toast.success('Role updated'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not update role'); }
  };
  const onKick = async (pid, name) => {
    if (!window.confirm(`Remove ${name} from the squad?`)) return;
    try { await api.post(`/arenax/squads/${squad.id}/members/${pid}/kick`); toast.success('Removed'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not remove'); }
  };
  return (
    <div className="space-y-2" data-testid="arenax-squad-members-tab">
      {members.map(m => (
        <div key={m.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-800 bg-slate-900/40">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-slate-950"
              style={{ background: m.role === 'captain' ? 'linear-gradient(135deg, #f59e0b, #ec4899)' : m.role === 'co_captain' ? 'linear-gradient(135deg, #06b6d4, #a855f7)' : 'linear-gradient(135deg, #06b6d4, #10b981)' }}>
              {(m.player_name || 'P')[0].toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-bold text-slate-100 flex items-center gap-1.5">
                {m.player_name}
                {m.role === 'captain' && <Crown className="h-3.5 w-3.5 text-amber-300" />}
                {m.player_id === myId && <span className="text-[10px] tracking-wider uppercase text-cyan-300">(you)</span>}
              </div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">
                {m.role.replace('_', ' ')} · {m.games_played} games · {m.no_shows} no-shows
              </div>
            </div>
          </div>
          {isCaptain && m.player_id !== myId && m.role !== 'captain' && (
            <div className="flex items-center gap-1">
              <button onClick={() => onPromote(m.player_id)} className="text-[10px] px-2 py-1 rounded-md text-amber-300 hover:bg-amber-500/10 font-bold uppercase tracking-wider" data-testid={`arenax-squad-promote-${m.player_id}`}>
                {m.role === 'co_captain' ? 'Demote' : 'Promote'}
              </button>
              <button onClick={() => onKick(m.player_id, m.player_name)} className="text-[10px] px-2 py-1 rounded-md text-rose-300 hover:bg-rose-500/10 font-bold uppercase tracking-wider" data-testid={`arenax-squad-kick-${m.player_id}`}>
                Remove
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RecurringTab({ squad, reload }) {
  const recurring = squad.recurring || [];
  const onDisable = async (rid) => {
    if (!window.confirm('Disable this recurring schedule? Future games will be cancelled and slots released.')) return;
    try { const r = await api.delete(`/arenax/squads/${squad.id}/recurring/${rid}`); toast.success(`Disabled. ${r.data.cancelled_games} future games cancelled.`); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not disable'); }
  };
  return (
    <div className="space-y-2" data-testid="arenax-squad-recurring-tab">
      {recurring.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-amber-500/20 p-10 text-center">
          <Repeat className="h-10 w-10 text-amber-400/50 mx-auto mb-3" />
          <div className="text-sm text-slate-300 font-bold mb-2">No recurring games yet</div>
          <div className="text-xs text-slate-500">Hit "Schedule Weekly Game" above to set your squad's ritual.</div>
        </div>
      ) : recurring.map(r => (
        <div key={r.id} className="flex items-center justify-between p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="h-4 w-4 text-amber-300" />
              <span className="text-sm font-bold text-slate-100">Every {DAY_LABELS[r.day_of_week]}</span>
            </div>
            <div className="text-xs text-slate-400">{r.start_time}–{r.end_time} · {r.sport} · ₹{r.entry_fee_per_player}/player · {r.min_players}-{r.max_players} players</div>
          </div>
          <button onClick={() => onDisable(r.id)} className="text-[10px] px-3 py-1.5 rounded-md text-rose-300 hover:bg-rose-500/10 font-bold uppercase tracking-wider">Disable</button>
        </div>
      ))}
    </div>
  );
}

function AddRecurringDialog({ squad, onClose, onDone }) {
  const [venues, setVenues] = useState([]);
  const [form, setForm] = useState({
    day_of_week: 5, start_time: '07:00', end_time: '08:00',
    venue_id: squad.home_venue_id || '', min_players: 4, max_players: 10, entry_fee_per_player: 150, needs_note: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/arenax/venues?sport=${encodeURIComponent(squad.primary_sport)}`).then(r => setVenues(r.data.venues || [])).catch(() => {});
  }, [squad.primary_sport]);

  const submit = async () => {
    if (!form.venue_id) { toast.error('Pick a venue'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/arenax/squads/${squad.id}/recurring`, form);
      toast.success(`Scheduled! ${r.data.published.length} weekly games published.`);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not schedule'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-2xl border border-cyan-500/20 max-w-md w-full"
        style={{ background: 'linear-gradient(160deg, #0a1424, #02050b)' }}>
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <h3 className="text-base font-bold text-slate-100 flex items-center gap-2"><Repeat className="h-4 w-4 text-amber-300" />Schedule Weekly Game</h3>
          <button onClick={onClose}><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Day of week</label>
            <div className="flex gap-1 flex-wrap">
              {DAY_LABELS.map((d, i) => (
                <button key={i} onClick={() => setForm({ ...form, day_of_week: i })}
                  data-testid={`arenax-rec-day-${i}`}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-bold ${form.day_of_week === i ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 border border-slate-700 text-slate-300'}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Start</label>
              <input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })}
                data-testid="arenax-rec-start"
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">End</label>
              <input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })}
                data-testid="arenax-rec-end"
                className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Home venue</label>
            <select value={form.venue_id} onChange={e => setForm({ ...form, venue_id: e.target.value })}
              data-testid="arenax-rec-venue"
              className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm">
              <option value="">— pick a venue —</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name} · {v.city}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Min</label>
              <input type="number" value={form.min_players} onChange={e => setForm({ ...form, min_players: Number(e.target.value) })}
                className="w-full px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Max</label>
              <input type="number" value={form.max_players} onChange={e => setForm({ ...form, max_players: Number(e.target.value) })}
                className="w-full px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
            </div>
            <div>
              <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Fee ₹</label>
              <input type="number" value={form.entry_fee_per_player} onChange={e => setForm({ ...form, entry_fee_per_player: Number(e.target.value) })}
                className="w-full px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-wider uppercase text-slate-400 font-bold mb-1.5 block">Squad need (optional)</label>
            <input type="text" value={form.needs_note} onChange={e => setForm({ ...form, needs_note: e.target.value })}
              placeholder="e.g. Need 2 defenders + a goalkeeper"
              maxLength={120}
              className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm placeholder:text-slate-600" />
          </div>
          <div className="text-[10px] text-slate-500 italic">Next 4 weekly games will be auto-published. You become the host of each.</div>
        </div>
        <div className="p-5 border-t border-slate-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="arenax-rec-submit"
            className="px-5 py-2 rounded-lg text-xs font-bold text-slate-950 flex items-center gap-1.5"
            style={{ background: 'var(--ax-primary-gradient)' }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}Schedule & Publish
          </button>
        </div>
      </div>
    </div>
  );
}

function SquadChat({ squadId, userId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const sinceRef = useRef(null);
  const scrollRef = useRef(null);

  const load = async () => {
    const q = sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : '';
    try {
      const r = await api.get(`/arenax/squads/${squadId}/messages${q}`);
      const fresh = r.data.messages || [];
      if (fresh.length) {
        setMsgs(prev => sinceRef.current ? [...prev, ...fresh] : fresh);
        sinceRef.current = fresh[fresh.length - 1].created_at;
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try { await api.post(`/arenax/squads/${squadId}/messages`, { text: t }); setText(''); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-cyan-500/20 p-5" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }} data-testid="arenax-squad-chat">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-4 w-4 text-cyan-300" />
        <h3 className="text-sm font-bold text-slate-100">Squad Chat</h3>
        <span className="ml-auto text-[10px] text-emerald-400 flex items-center gap-1.5">
          <span className="relative h-1.5 w-1.5"><span className="absolute inset-0 rounded-full bg-emerald-400" /><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" /></span>
          LIVE
        </span>
      </div>
      <div ref={scrollRef} className="h-96 overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-16">No messages yet. Drop the first one 💬</div>
        ) : msgs.map(m => {
          const isSelf = m.player_id === userId;
          const isSystem = m.kind === 'system';
          if (isSystem) return <div key={m.id} className="text-center text-[10px] text-slate-500 italic tracking-wide py-1">{m.text}</div>;
          return (
            <div key={m.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-xl px-3 py-2 ${isSelf ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-100'}`}>
                {!isSelf && <div className="text-[10px] font-bold text-cyan-300 mb-0.5">{m.player_name}</div>}
                <div className="text-sm break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Message your squad..."
          data-testid="arenax-squad-chat-input"
          className="flex-1 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500 focus:outline-none text-sm text-slate-100 placeholder:text-slate-600" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="arenax-squad-chat-send"
          className="px-4 rounded-xl font-bold text-slate-950 transition-all disabled:opacity-40"
          style={{ background: 'var(--ax-primary-gradient)' }}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
